/**
 * MyProfileDemo — "Living Archive" cinematic redesign (Apr 30 2026).
 *
 * Replaces the 2016-era white-card-grid profile with a deep-black
 * cinematic showcase per the design blueprint:
 *   - Hero: massive serif name in gold over deep-space dust background,
 *     avatar floating off-axis with a 1px cyan glow ring
 *   - Stats become a "Control Room" horizontal band — hairline gold
 *     dividers, no boxes, oversized serif numerals
 *   - Walkthrough is a glassmorphism HUD strip (cyan left edge)
 *   - Methodology certificates are 3D-tilt artifact cards (Framer Motion)
 *   - Walked chains become a vertical glowing-cyan timeline that staggers
 *     into view as you scroll
 *   - Saved highlights as bordered serif blockquotes on glass
 *
 * Data structures (PROFILE / FOUNDATION_BADGE / ERA_BADGES / WALKED_CHAINS
 * / HIGHLIGHTS / STATS / WALKTHROUGH_STEPS) are untouched. All previous
 * test IDs preserved + four blueprint test IDs added (profile-page,
 * profile-cert-card, profile-reading-log-item, profile-stat-dial).
 */
import { useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { User, Award, BookOpen, FileText, BookOpenCheck, Sparkles, ArrowRight, MapPin, ScrollText, ChevronRight } from 'lucide-react';

// ─── Mock data (unchanged) ─────────────────────────────────────────
const PROFILE = {
  name: 'Sample Reader',
  handle: '@sample_reader',
  joined: 'November 2025',
  location: 'San Francisco, CA',
  bio: 'Walking the chain. Methodology before narrative. Sources before story.',
};

const FOUNDATION_BADGE = {
  tier: 'Foundation',
  granted: 'Apr 22 2026',
  description: 'Walked the Walker → Toolmaker → Spark → Symbolic Leap canonical spine. Cited at least one primary source. Investigated one contradiction.',
};

const ERA_BADGES = [
  { era: 'Deep Time',  status: 'earned',      pct: 100, desc: '4 / 4 chains walked, 11 sources read' },
  { era: 'Ancient',    status: 'in_progress', pct: 60,  desc: '3 / 5 chains walked' },
  { era: 'Classical',  status: 'in_progress', pct: 25,  desc: '1 / 4 chains walked' },
  { era: 'Medieval',   status: 'locked',      pct: 0,   desc: 'Earn the Classical badge to unlock' },
];

const WALKED_CHAINS = [
  { id: 'walker',     title: 'The First Walker',      era: 'Deep Time', walkedAt: 'Apr 18 2026', minutes: 14, contradiction: 'investigated' },
  { id: 'toolmaker',  title: 'The First Toolmaker',   era: 'Deep Time', walkedAt: 'Apr 19 2026', minutes: 16, contradiction: 'investigated' },
  { id: 'spark',      title: 'The First Spark',       era: 'Deep Time', walkedAt: 'Apr 20 2026', minutes: 17, contradiction: 'investigated' },
  { id: 'symbolic',   title: 'The Symbolic Leap',     era: 'Deep Time', walkedAt: 'Apr 21 2026', minutes: 19, contradiction: 'investigated' },
  { id: 'alexandria', title: 'Library of Alexandria', era: 'Classical', walkedAt: 'Apr 24 2026', minutes: 12, contradiction: 'in_progress', isInProgress: true },
];

const HIGHLIGHTS = [
  { chain: 'The First Walker',    text: '"...the Mission Paléoanthropologique noted the cranial fragments before the foreman could fold the canvas."', sourceTag: '[S_BRUNET_2002]' },
  { chain: 'The First Toolmaker', text: '"3.3 million years ago — Lomekwi 3 pushed the toolmaking origin back further than the genus Homo."',       sourceTag: '[S_HARMAND_2015]' },
  { chain: 'The First Spark',     text: '"Wonderwerk Cave fire residue: not naturally occurring. The smoke had a story."',                              sourceTag: '[S_BERNA_2012]' },
];

const STATS = [
  { label: 'Chains walked',               value: '4',     Icon: BookOpen },
  { label: 'Contradictions investigated', value: '4',     Icon: ScrollText },
  { label: 'Sources cited',               value: '17',    Icon: FileText },
  { label: 'Era certs',                   value: '1 / 9', Icon: Award },
];

const WALKTHROUGH_STEPS = [
  {
    title: 'Your profile is your reading log',
    body: 'Every chain you walk lands here. Every primary source you click is logged. Every contradiction you investigate counts toward the next era certificate.',
  },
  {
    title: 'Era certificates beat scoreboards',
    body: 'TSOGN does not gamify reading. Era certificates are issued only when a methodology threshold is met — chains walked + sources cited + contradictions investigated. There are no streaks. There are no points.',
  },
  {
    title: 'Highlights are sourced',
    body: 'Anything you save here keeps a pointer to its source registry tag (e.g. [S_BRUNET_2002]). Your highlights are a citation table, not a pull-quote feed.',
  },
];

// ─── ArtifactCard — 3D magnetic tilt on hover (Framer Motion) ──────
function ArtifactCard({ children, testId, className = '', accent = 'gold', earned = true }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), { stiffness: 200, damping: 20 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]),  { stiffness: 200, damping: 20 });
  const accentColor = accent === 'cyan' ? '#2F8A92' : '#D4A84A';
  const glareX = useTransform(x, [-0.5, 0.5], ['0%', '100%']);
  const glareY = useTransform(y, [-0.5, 0.5], ['0%', '100%']);

  function handleMouse(e) {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  }
  function handleLeave() { x.set(0); y.set(0); }

  return (
    <motion.div
      ref={ref}
      data-testid={testId}
      onMouseMove={handleMouse}
      onMouseLeave={handleLeave}
      style={{ rotateX, rotateY, transformPerspective: 1000 }}
      className={`relative overflow-hidden rounded-lg border ${earned ? 'border-[#D4A84A]/35' : 'border-white/10'} backdrop-blur-xl ${className}`}
    >
      <div className="absolute inset-0 bg-[#0A0A0A]/60 pointer-events-none" />
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(220px circle at ${glareX} ${glareY}, ${accentColor}22, transparent 65%)`,
        }}
      />
      <div className="relative z-10">{children}</div>
    </motion.div>
  );
}

// ─── HUD Walkthrough strip ─────────────────────────────────────────
function HUDWalkthrough({ stepIdx, setStepIdx, total, step, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="relative mb-10 overflow-hidden rounded-r-md backdrop-blur-xl"
      data-testid="profile-walkthrough"
    >
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#2F8A92] shadow-[0_0_12px_2px_rgba(47,138,146,0.55)]" />
      <div className="relative pl-6 pr-5 py-4 flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkles size={11} className="text-[#2F8A92]" />
            <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#2F8A92]/85">
              Walkthrough · {stepIdx + 1} / {total}
            </span>
            <div className="flex gap-1.5 ml-2">
              {Array.from({ length: total }).map((_, i) => (
                <span
                  key={i}
                  className={`h-1 w-5 rounded-full transition-colors ${i <= stepIdx ? 'bg-[#D4A84A]' : 'bg-white/15'}`}
                />
              ))}
            </div>
          </div>
          <div className="font-historical text-white text-lg md:text-xl mb-1">{step.title}</div>
          <p className="font-mono text-xs md:text-sm text-zinc-300/85 leading-relaxed">{step.body}</p>
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          {stepIdx < total - 1 ? (
            <button
              onClick={() => setStepIdx(stepIdx + 1)}
              className="inline-flex items-center gap-1 font-mono text-xs text-[#2F8A92] hover:text-[#D4A84A] transition-colors"
              data-testid="profile-walkthrough-next"
            >
              Next <ArrowRight size={12} />
            </button>
          ) : (
            <button
              onClick={onClose}
              className="font-mono text-xs text-[#D4A84A] hover:text-white transition-colors"
              data-testid="profile-walkthrough-close"
            >
              Got it
            </button>
          )}
          <button
            onClick={onClose}
            className="font-mono text-[10px] text-white/40 hover:text-white/70 transition-colors"
            data-testid="profile-walkthrough-dismiss"
          >
            Skip walkthrough
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────
export default function MyProfileDemo() {
  const [stepIdx, setStepIdx] = useState(0);
  const [walkthroughOpen, setWalkthroughOpen] = useState(true);

  return (
    <div
      className="relative min-h-screen bg-[#0A0A0A] text-white overflow-hidden"
      data-testid="profile-page"
    >
      {/* Ambient deep-space background — radial gold dust + grid hairlines */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          backgroundImage:
            'radial-gradient(ellipse 1200px 500px at 25% 15%, rgba(212,168,74,0.10), transparent 60%),' +
            'radial-gradient(ellipse 900px 400px at 80% 0%, rgba(47,138,146,0.06), transparent 60%),' +
            'radial-gradient(ellipse 800px 800px at 50% 110%, rgba(212,168,74,0.05), transparent 70%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(212,168,74,0.5) 1px, transparent 1px),' +
            'linear-gradient(to bottom, rgba(212,168,74,0.5) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      <div className="relative px-4 md:px-10 lg:px-16 py-10 md:py-16 max-w-7xl mx-auto" data-testid="my-profile-demo">
        {/* Eyebrow */}
        <div className="mb-6 flex items-center gap-3 font-mono text-[10px] tracking-[0.32em] uppercase">
          <span className="text-[#D4A84A]">Demo Mode</span>
          <span className="text-white/25">·</span>
          <span className="text-white/45">populated sample profile</span>
          <span className="ml-2 inline-flex items-center gap-1.5 text-[#2F8A92]/70">
            <span className="h-1.5 w-1.5 rounded-full bg-[#2F8A92] shadow-[0_0_8px_rgba(47,138,146,0.8)] animate-pulse" />
            Living archive
          </span>
        </div>

        {/* ─── HERO ─────────────────────────────────────────────── */}
        <motion.header
          initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative mb-14 md:mb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end"
          data-testid="profile-hero"
        >
          {/* Avatar — floats off-axis, cyan glow ring */}
          <div className="lg:col-span-3 relative">
            <div className="relative w-32 h-32 md:w-40 md:h-40">
              <div className="absolute inset-0 rounded-full border border-[#2F8A92]/60 shadow-[0_0_40px_rgba(47,138,146,0.25)]" />
              <div className="absolute inset-1 rounded-full border border-[#D4A84A]/30" />
              <div className="absolute inset-3 rounded-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center">
                <User size={56} className="text-[#D4A84A]/85" strokeWidth={1} />
              </div>
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#2F8A92] shadow-[0_0_12px_rgba(47,138,146,0.9)]" />
            </div>
          </div>

          <div className="lg:col-span-9 min-w-0">
            <h1
              className="font-historical text-[#D4A84A] text-5xl md:text-7xl lg:text-[5.5rem] font-light tracking-tight leading-[0.95]"
              data-testid="profile-name"
            >
              {PROFILE.name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 font-mono text-xs tracking-widest text-white/55 uppercase">
              <span className="text-[#2F8A92]/80">{PROFILE.handle}</span>
              <span className="text-white/20">·</span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={11} className="text-[#D4A84A]/70" /> {PROFILE.location}
              </span>
              <span className="text-white/20">·</span>
              <span>Member since {PROFILE.joined}</span>
            </div>
            <p className="mt-5 font-historical italic text-white/75 text-base md:text-lg max-w-2xl leading-relaxed border-l border-[#D4A84A]/30 pl-4">
              “{PROFILE.bio}”
            </p>
          </div>
        </motion.header>

        {walkthroughOpen && (
          <HUDWalkthrough
            stepIdx={stepIdx}
            setStepIdx={setStepIdx}
            total={WALKTHROUGH_STEPS.length}
            step={WALKTHROUGH_STEPS[stepIdx]}
            onClose={() => setWalkthroughOpen(false)}
          />
        )}

        {/* ─── STATS — Control Room band ──────────────────────── */}
        <section
          className="relative mb-16 border-y border-[#D4A84A]/20 py-7 md:py-9 grid grid-cols-2 md:grid-cols-4"
          data-testid="profile-stats"
        >
          {STATS.map(({ label, value, Icon }, idx) => (
            <motion.div
              key={label}
              data-testid="profile-stat-dial"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, delay: idx * 0.08 }}
              className={`relative px-5 md:px-7 ${idx > 0 ? 'md:border-l border-[#D4A84A]/15' : ''} ${idx > 0 && idx % 2 !== 0 ? 'border-l border-[#D4A84A]/15' : ''}`}
            >
              <Icon size={14} className="text-[#D4A84A]/80 mb-3" strokeWidth={1.5} />
              <div className="font-historical text-4xl md:text-5xl lg:text-6xl text-white leading-none tabular-nums">
                {value}
              </div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/50 mt-3">
                {label}
              </div>
            </motion.div>
          ))}
        </section>

        {/* ─── METHODOLOGY CERTIFICATES ──────────────────────────── */}
        <section className="mb-16" data-testid="profile-cert">
          <div className="flex items-center gap-3 mb-6">
            <Award size={14} className="text-[#D4A84A]" strokeWidth={1.5} />
            <h2 className="font-mono text-[11px] tracking-[0.32em] uppercase text-white/70">
              Methodology Archive
            </h2>
            <div className="flex-1 h-px bg-[#D4A84A]/15" />
          </div>

          {/* Foundation tier — flagship artifact card */}
          <ArtifactCard
            testId="profile-foundation-cert profile-cert-card"
            accent="gold"
            className="mb-6"
            earned
          >
            <div className="p-6 md:p-8 flex flex-col md:flex-row items-start gap-5">
              <div className="flex-shrink-0">
                <div className="relative w-14 h-14 rounded-full border border-[#D4A84A]/50 flex items-center justify-center shadow-[0_0_20px_rgba(212,168,74,0.18)]">
                  <BookOpenCheck size={22} className="text-[#D4A84A]" strokeWidth={1.5} />
                </div>
              </div>
              <div className="flex-1">
                <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#D4A84A] mb-2">
                  Foundation Tier · Earned
                </div>
                <div className="font-historical text-white text-2xl md:text-3xl leading-tight mb-3">
                  Methodology Certificate, Foundation
                </div>
                <p className="font-mono text-xs md:text-sm text-white/65 leading-relaxed max-w-2xl">
                  {FOUNDATION_BADGE.description}
                </p>
                <div className="mt-3 font-mono text-[10px] tracking-widest uppercase text-white/40">
                  Granted {FOUNDATION_BADGE.granted}
                </div>
              </div>
              <button
                data-testid="profile-foundation-claim"
                className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4A84A] border border-[#D4A84A]/50 px-4 py-2.5 rounded-md hover:bg-[#D4A84A] hover:text-black transition-all whitespace-nowrap"
              >
                View / Print
              </button>
            </div>
          </ArtifactCard>

          {/* Era certs grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="profile-era-badges">
            {ERA_BADGES.map(({ era, status, pct, desc }, idx) => {
              const earned = status === 'earned';
              const inProgress = status === 'in_progress';
              const accent = earned ? 'gold' : 'cyan';
              return (
                <motion.div
                  key={era}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: idx * 0.06 }}
                >
                  <ArtifactCard
                    testId={`era-${era.toLowerCase().replace(' ', '-')} profile-cert-card`}
                    accent={accent}
                    earned={earned}
                    className={status === 'locked' ? 'opacity-50' : ''}
                  >
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-historical text-white text-xl">{era}</div>
                        <div
                          className={`font-mono text-[10px] tracking-[0.25em] uppercase ${
                            earned ? 'text-[#D4A84A]' : inProgress ? 'text-[#2F8A92]' : 'text-white/35'
                          }`}
                        >
                          {earned ? '✓ Earned' : inProgress ? `${pct}%` : 'Locked'}
                        </div>
                      </div>
                      <p className="font-mono text-xs text-white/55 leading-relaxed mb-3">{desc}</p>
                      {status !== 'locked' && (
                        <div className="h-[2px] bg-white/8 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${pct}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, ease: 'easeOut', delay: 0.2 }}
                            className={earned ? 'h-full bg-[#D4A84A]' : 'h-full bg-[#2F8A92]'}
                          />
                        </div>
                      )}
                    </div>
                  </ArtifactCard>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ─── WALKED CHAINS — vertical glowing timeline ──────────── */}
        <section className="mb-16" data-testid="profile-walked-chains">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen size={14} className="text-[#2F8A92]" strokeWidth={1.5} />
            <h2 className="font-mono text-[11px] tracking-[0.32em] uppercase text-white/70">
              Reading Log · Chain Timeline
            </h2>
            <div className="flex-1 h-px bg-[#2F8A92]/15" />
          </div>
          <div className="relative pl-6 md:pl-10">
            {/* Vertical glowing line */}
            <div className="absolute top-0 bottom-0 left-1.5 md:left-3 w-px bg-gradient-to-b from-[#2F8A92]/0 via-[#2F8A92]/55 to-[#D4A84A]/0" />
            <ul className="space-y-5">
              {WALKED_CHAINS.map((c, idx) => (
                <motion.li
                  key={c.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.55, delay: idx * 0.08, ease: 'easeOut' }}
                  data-testid={`walked-${c.id} profile-reading-log-item`}
                  className="relative group cursor-pointer"
                >
                  {/* Node dot */}
                  <span
                    className={`absolute -left-[18px] md:-left-[26px] top-1.5 h-2.5 w-2.5 rounded-full ${
                      c.isInProgress
                        ? 'bg-[#2F8A92] shadow-[0_0_12px_rgba(47,138,146,0.9)]'
                        : 'bg-[#D4A84A] shadow-[0_0_12px_rgba(212,168,74,0.7)]'
                    }`}
                  />
                  <div className="flex items-center gap-4 py-2 group-hover:bg-white/[0.025] rounded-md px-3 -ml-3 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="font-historical text-white text-lg md:text-xl leading-tight">
                        {c.title}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5 font-mono text-[10px] tracking-widest uppercase text-white/45">
                        <span className="text-[#D4A84A]/80">{c.era}</span>
                        <span className="text-white/20">·</span>
                        <span>{c.walkedAt}</span>
                        <span className="text-white/20">·</span>
                        <span>{c.minutes} min walk</span>
                        <span className="text-white/20">·</span>
                        <span className={c.isInProgress ? 'text-[#2F8A92]' : 'text-[#D4A84A]/80'}>
                          contradiction {c.isInProgress ? 'pending' : 'investigated'}
                        </span>
                      </div>
                    </div>
                    <ChevronRight
                      size={18}
                      className="text-white/30 group-hover:text-[#D4A84A] group-hover:translate-x-0.5 transition-all flex-shrink-0"
                    />
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        </section>

        {/* ─── HIGHLIGHTS — sourced blockquotes ────────────────── */}
        <section className="mb-12" data-testid="profile-highlights">
          <div className="flex items-center gap-3 mb-6">
            <ScrollText size={14} className="text-[#D4A84A]" strokeWidth={1.5} />
            <h2 className="font-mono text-[11px] tracking-[0.32em] uppercase text-white/70">
              Saved Highlights · Citation Table
            </h2>
            <div className="flex-1 h-px bg-[#D4A84A]/15" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {HIGHLIGHTS.map((h, idx) => (
              <motion.blockquote
                key={idx}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: idx * 0.08 }}
                data-testid={`highlight-${idx}`}
                className="relative rounded-md border border-white/10 bg-white/[0.02] backdrop-blur-md p-5 hover:border-[#D4A84A]/40 transition-colors"
              >
                <div className="absolute top-3 left-3 font-historical text-3xl text-[#D4A84A]/40 leading-none">“</div>
                <p className="font-historical text-white/85 text-base leading-relaxed pl-6 italic">{h.text}</p>
                <div className="mt-3 pl-6 font-mono text-[10px] tracking-widest uppercase">
                  <span className="text-[#D4A84A]">{h.sourceTag}</span>
                  <span className="text-white/30"> · </span>
                  <span className="text-white/50">{h.chain}</span>
                </div>
              </motion.blockquote>
            ))}
          </div>
        </section>

        {/* Footer */}
        <footer
          className="border-t border-white/10 pt-7 text-center"
          data-testid="profile-footer"
        >
          <p className="font-mono text-[11px] tracking-widest uppercase text-white/35 leading-relaxed">
            Demo profile · sign up to start your real reading log — every walked chain logs here automatically.
          </p>
        </footer>
      </div>
    </div>
  );
}
